import { Button, Spinner } from '@mm/components';
import {
  useAutomationActions,
  useAutomationsCRUDActions,
} from '@mm/state/actions/automationActions';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import modalActions, {
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import { IAutomationState } from '@mm/state/reducers/automation';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import PAGES_HEAD_CONTENT from '../../../../../../common/components/MetaData/constants';
import MetaData from '../../../../../../common/components/MetaData/MetaData';
import { useBotChannels } from '../../../../../../common/hooks/use-bot-channels';
import { useInactiveBotActivator } from '../../../../../common/hooks/use-inactive-bot-activator';
import { AutomationHeader } from '../components/AutomationHeader/AutomationHeader';
import { ActionsList } from './ActionsList/ActionsList';
import styles from './AutomationEdit.module.css';
import { DialogueEditor } from './DialogueEditor/DialogueEditor';
import { InputTriggers } from './InputTriggers/InputTriggers';
import { NewInputTriggerChooser } from './InputTriggers/NewInputTriggerChooser';
import { useChannelConnection } from './InputTriggers/use-channel-connection';
import { useInputTriggersIdentification } from './InputTriggers/use-input-triggers-identification';
import { NeedsReviewAlert } from './NeedsReviewAlert/NeedsReviewAlert';
import { UpgradeWarning } from './UpgradeWarning/UpgradeWarning';
import { useAutomation } from './use-automation';
import { useNeedsReview } from './use-needs-review';
import { NeedsEmailAlert } from './NeedsEmailAlert/NeedsEmailAlert';
import { InactiveBotAlert } from '../../../../../components/InactiveBotAlert/InactiveBotAlert';
import { InactiveEmailAlert } from '../../../../../components/InactiveEmailAlert/InactiveEmailAlert';
import { Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';

type Props = {
  className?: string;
  automationData: ReturnType<typeof useAutomation>;
};

// tslint:disable-next-line:cyclomatic-complexity
export const AutomationEdit = ({ className, automationData }: Props) => {
  const { activateBotGuard } = useInactiveBotActivator();
  const { activateChannelGuard } = useBotChannels();
  const { actions, state } = automationData;

  const viewActions = useViewActions();
  const { openActivateEmailAccountModal } = useModalActions();

  const reduxState = useSelector<StoreState, IAutomationState>(
    state => state.automation,
  );
  const botId = useSelector(activeChatbotIdSelector);
  const sidebarMinimized = useSelector(
    (s: StoreState) => s.chatbotSidebarState.isMinimized,
  );
  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();
  const reduxActions = useAutomationActions();
  const automationCRUDActions = useAutomationsCRUDActions();
  const { isSMSConnected } = useChannelConnection(() => {});

  const campaignId = state.source?.id;
  const triggers = useInputTriggersIdentification(campaignId);
  const [addingInputTrigger, setAddingInputTrigger] = useState(false);
  const [disabledEditor, setDisabledEditor] = useState(false);
  const isAutomaticallyCreatedCampaign = Boolean(state.source?.system);

  const hideOpenMenus = () => {
    if (reduxState.action || addingInputTrigger) {
      reduxActions.hideActionsList();
      setAddingInputTrigger(false);
    }
  };

  const automationInputTriggersPlatforms = Array.from(
    new Set(
      triggers
        ?.map(t => {
          switch (t.type) {
            case 'webchatWidget':
              return 'webChat';
            case 'general':
              return t.label
                .toLocaleLowerCase()
                .substring(0, t.label.indexOf(' '));
            case 'contacts_csv':
              return isSMSConnected ? 'sms' : 'email';
            case 'getemails':
              return 'email';
            case 'audience':
              return;
            default:
              return t.type;
          }
        })
        .filter(t => t),
    ),
  );

  const onStart = async () => {
    await activateBotGuard();
    await activateChannelGuard(automationInputTriggersPlatforms);
    await actions.start();
  };

  const {
    destinationPageId: dialogueId,
    destinationPageGroupId: pageGroupId,
    scheduledAt,
    intervalInMin,
  } = state.source?.schedulersAttributes?.[0] || {};

  const retrieveEnableInputTriggers = useCallback(() => {
    campaignsOverviewCRUDActions.retrieveEnabledInputTriggers(botId);
  }, [botId, campaignsOverviewCRUDActions]);

  const dispatch: Dispatch<StoreState> = useDispatch();

  const onSoLDConfigureBtnClick = () => {
    hideOpenMenus();
    viewActions.showSlideoutMenu('automationSoLDSettings', {
      campaignId,
    });
  };

  const onCloneAutomationClick = () => {
    dispatch(modalActions.openCloneAutomationModal({ campaignId }));
  };

  const onDelete = () => {
    actions.deleteAutomation({});
  };

  const withTooltip = (
    children: React.ReactNode,
    placement?: TooltipPlacement,
  ) =>
    disabledEditor ? (
      <Tooltip
        overlayClassName={styles.antTooltip}
        popupVisible={disabledEditor}
        placement={placement || 'top'}
        trigger="hover"
        title={
          <div className={styles.tooltipContent}>
            To continue sending emails, please reconnect your email in your
            account settings
            <Button
              css={{ container: styles.tooltipButton }}
              color="transparent-no-shadow"
              onClick={e => {
                e?.preventDefault();
                openActivateEmailAccountModal();
              }}
            >
              Fix Email
            </Button>
          </div>
        }
      >
        <div>
          <div className={classNames({ [styles.noClick]: disabledEditor })}>
            {children}
          </div>
        </div>
      </Tooltip>
    ) : (
      children
    );

  const menu = [
    {
      disabled: false,
      children: 'Clone Automation',
      onClick: onCloneAutomationClick,
    },
    {
      disabled: isAutomaticallyCreatedCampaign,
      children: 'Delete Automation',
      onClick: onDelete,
    },
  ];

  useEffect(() => {
    retrieveEnableInputTriggers();
  }, [retrieveEnableInputTriggers]);

  const needsReview = useNeedsReview();
  const automationActions = useAutomationActions();

  const [elements, setElements] = useState([]);

  const onChange = (_, elements) => {
    setElements(elements);
  };

  useEffect(() => {
    automationActions.setReviewRequiredCount(null);

    return () => {
      automationActions.setReviewRequiredCount(null);
    };
  }, []);

  // cleanup review badge if user closed app earlier than request for campaign initiated
  useEffect(() => {
    if (needsReview.count === 0 && state.source?.needsReview) {
      actions.removeNeedsReview();
    }
  }, [needsReview.count, state.source?.needsReview]);

  useEffect(() => {
    if (elements.length) {
      const count = needsReview.deriveCount({ triggers, elements });
      automationActions.setReviewRequiredCount(count);
    }
  }, [triggers?.length, elements?.length]);

  useEffect(() => {
    if (botId) {
      automationCRUDActions.getExistingInputTriggers(botId);
    }
  }, []);

  return (
    <div className={classNames(className, styles.container)}>
      <MetaData
        title={PAGES_HEAD_CONTENT.AUTOMATIONS.title}
        description={PAGES_HEAD_CONTENT.AUTOMATIONS.meta.description}
      />

      <AutomationHeader
        state={state}
        actions={actions}
        onStart={onStart}
        menu={menu}
        withTooltip={withTooltip}
        disabledActions={disabledEditor}
      />

      <InactiveBotAlert />

      <InactiveEmailAlert state={state} onChange={setDisabledEditor} />

      {!!needsReview.count && <NeedsReviewAlert count={needsReview.count} />}
      <UpgradeWarning pageGroupId={pageGroupId} />
      {triggers?.find(t => t.type === 'getemails') && <NeedsEmailAlert />}

      <div
        className={classNames(styles.editor, {
          [styles.disabledEditor]: disabledEditor,
        })}
      >
        {state.busy === 'fetching' && <Spinner size={24} standalone />}

        {campaignId && (
          <div>
            {triggers === null && state.busy === 'fetching' && (
              <Spinner size={24} standalone />
            )}
            {!!triggers && (
              <InputTriggers
                campaignId={campaignId}
                triggers={triggers}
                onAddInputTriggerClicked={() => setAddingInputTrigger(true)}
                withTooltip={withTooltip}
              />
            )}
          </div>
        )}

        {dialogueId &&
          campaignId &&
          withTooltip(
            <DialogueEditor
              className={styles.flowchart}
              dialogueId={dialogueId}
              campaignId={campaignId}
              pageGroupId={pageGroupId}
              scheduledAt={scheduledAt}
              intervalInMin={intervalInMin}
              onPaneClick={hideOpenMenus}
              onMoveStart={hideOpenMenus}
              onChange={onChange}
              styles={{
                container: classNames({ [styles.noClick]: disabledEditor }),
              }}
            />,
          )}

        {/* Note: Temporary hiding till PMs figure out what they want */}

        {/* <Step
          number={4}
          title="SoLD (Signs of Life Detector) Funnel"
          className={styles.email}
        >
          <span className={styles.emailSubtitle}>
            Move contacts to different funnels (or remove them entirely) based on
            their level of engagement
          </span>
          <Button
            color="blue"
            leftIcon={<SettingFilled />}
            onClick={onSoLDConfigureBtnClick}
          >
            Configure SoLD
          </Button>
        </Step> */}

        {campaignId && addingInputTrigger && (
          <div className={styles.modal}>
            <NewInputTriggerChooser
              triggers={triggers}
              className={classNames(styles.actionsList, {
                [styles.sidebarExpanded]: !sidebarMinimized,
              })}
              campaignId={campaignId}
              setAddingInputTrigger={setAddingInputTrigger}
              onClose={() => setAddingInputTrigger(false)}
            />
          </div>
        )}

        <div className={styles.modal}>
          {campaignId && reduxState.action && (
            <ActionsList
              className={classNames(styles.actionsList, {
                [styles.sidebarExpanded]: !sidebarMinimized,
              })}
              campaignId={campaignId}
              {...reduxState.action}
            />
          )}
        </div>
      </div>
    </div>
  );
};
